import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, MailIcon } from 'lucide-react';
import { business } from '../data/business';
import { services } from '../data/services';
import { ContactBand } from '../components/ContactBand';
import { PageIntro } from '../components/PageIntro';
import { useSeo } from '../utils/seo';
export function Services() {
  useSeo({
    title: 'Gordijnrailsystemen, elektrische en gemotoriseerde gordijnrails | Goelst',
    description: 'Overzicht van onze gordijnrails: gordijnrailsystemen, elektrische en gemotoriseerde gordijnrails, projectmatige rails, zorggordijnrails en commerciële gordijnrails.',
    path: '/diensten'
  });
  return <>
      <PageIntro breadcrumb="Gordijnrails" h1="Gordijnrails en gordijnrailsystemen voor professionele projecten" lead="Van een enkele handbediende baan tot een gemotoriseerd systeem dat met het gebouw meebeweegt. Hieronder vindt u per categorie wat wij leveren, waar het systeem voor bedoeld is en welke keuzes er in de uitwerking gemaakt worden." meta="Handbediend · elektrisch · gemotoriseerd · projectmatig · zorg · commercieel" data-id="element-413" data-mp="64oi0o9u94bq" />

      <nav aria-label="Overzicht van gordijnrailcategorieën" className="border-b border-line bg-canvas" data-id="element-414" data-mp="1fprcbepjm8i2">
        <div className="mx-auto max-w-shell px-6 py-8" data-id="element-415" data-mp="1kgr82gmxekiz">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-mute" data-id="element-416" data-mp="1khivh46ddby6">Op deze pagina</h2>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-3" data-id="element-417" data-mp="1phonwmkgr2hb">
            {services.map(service => <li key={service.id} data-id="element-418" data-mp="1zdia6xnziaca">
                <a href={`#${service.id}`} className="text-[15px] font-medium text-ink transition-colors hover:text-accent" data-id="element-419" data-mp="1zd8fiwrzvf09">
                  <span className="mr-2 font-display text-[13px] text-accent" data-id="element-420" data-mp="4zquphiz4w8x">{service.index}</span>
                  {service.title}
                </a>
              </li>)}
          </ul>
        </div>
      </nav>

      <section className="bg-white" aria-label="Onze gordijnrailcategorieën" data-id="element-421" data-mp="1cd19dyg2ajya">
        {services.map((service, i) => <article key={service.id} id={service.id} className={`scroll-mt-28 border-b border-line ${i % 2 === 1 ? 'bg-canvas' : 'bg-white'}`} data-id="element-422" data-mp="17eg9k04q22ad" data-mpi={i}>
            <div className="mx-auto max-w-shell px-6 py-16 md:py-20" data-id="element-423" data-mp="1ha9v290f564a">
              <div className="grid gap-10 lg:grid-cols-[0.42fr_1fr] lg:gap-16" data-id="element-424" data-mp="1vaawbpklifka">
                <div data-id="element-425" data-mp="1qc0x93vr2ute">
                  <span className="font-display text-[13px] font-600 uppercase tracking-[0.2em] text-accent" data-id="element-426" data-mp="1qcj0wlkqsbbc">
                    {service.index}
                  </span>
                  <h2 className="mt-4 font-display text-3xl font-700 leading-[1.08] tracking-tightest text-ink md:text-[38px]" data-id="element-427" data-mp="201kfc7bn2kn7">
                    {service.title}
                  </h2>
                  <p className="mt-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-428" data-mp="2dyz9tqgr1im1">{service.summary}</p>
                  {service.image && <figure className="mt-8" data-id="element-429" data-mp="2snr9341yh3ac">
                      <img src={service.image} alt={service.imageAlt} className={`w-full ${service.id === 'gordijnrailsystemen' || service.id === 'elektrische-gordijnrails' || service.id === 'projectmatige-gordijnrails' ? 'bg-shade object-contain p-8' : 'h-64 object-cover md:h-72'}`} loading="lazy" width={1000} height={1000} data-id="element-430" data-mp="2slxyxes4w0xl" />
                      <figcaption className="mt-2 text-[13px] text-ink-mute" data-id="element-431" data-mp="24zhrkebrz1p0">
                        Illustratief beeld bij deze categorie.
                      </figcaption>
                    </figure>}
                </div>

                <div data-id="element-432" data-mp="1vzum9hfxdbqo">
                  <div className="space-y-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-433" data-mp="1nwsrjfs6hbrr">
                    {service.body.map(paragraph => <p key={paragraph.slice(0, 40)} data-id="element-434" data-mp="1izailpe5uwjr">{paragraph}</p>)}
                  </div>
                  <div className="mt-9 border-t border-line pt-7" data-id="element-435" data-mp="1e5g02paaknm9">
                    <h3 className="font-display text-[15px] font-600 uppercase tracking-[0.14em] text-ink" data-id="element-436" data-mp="1a4mbrusxt6su">
                      Kenmerken
                    </h3>
                    <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-437" data-mp="107hp7tv6j9bi">
                      {service.bullets.map(bullet => <li key={bullet} className="flex gap-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-438" data-mp="v7mxxp7hshpx">
                          <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-439" data-mp="r7uuts0vb3ry" />
                          <span data-id="element-440" data-mp="2pl4mnfjmvkmj">{bullet}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-441" data-mp="2lizrudhg8hyb">
                    <a href={`mailto:${business.email}?subject=Vraag%20over%20${encodeURIComponent(service.title)}`} className="inline-flex items-center gap-2 bg-accent px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-accent-dark" data-id="element-442" data-mp="7ayxs3jx124b">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-443" data-mp="3kjuza24ykepy" />
                      Vraag over {service.title.toLowerCase()}
                    </a>
                    <a href={`tel:${business.phoneLink}`} className="text-[15px] font-semibold text-ink underline decoration-accent underline-offset-4" data-id="element-444" data-mp="3piy6nt8eux13">
                      Of bel {business.phoneDisplay}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section className="border-b border-line bg-ink text-white" aria-labelledby="keuzehulp" data-id="element-445" data-mp="1duyigjtrm654">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-20" data-id="element-446" data-mp="39ycxjgeg6hho">
          <h2 id="keuzehulp" className="max-w-3xl font-display text-3xl font-700 leading-[1.1] tracking-tightest md:text-[38px]" data-id="element-447" data-mp="35twwbsfs3y8s">
            Welke uitvoering past bij uw situatie?
          </h2>
          <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-white/75" data-id="element-448" data-mp="3ewo9bwoaz22j">
            Drie vragen bepalen in de meeste projecten de richting. De antwoorden erop leiden vaak al tot één
            duidelijke categorie.
          </p>
          <div className="mt-12 overflow-x-auto" data-id="element-449" data-mp="2m4uv2art0qnz">
            <table className="w-full min-w-[640px] border-collapse text-left" data-id="element-450" data-mp="30wn7adwaxolh">
              <caption className="sr-only" data-id="element-451" data-mp="2w4dfstfjggh3">
                Vergelijking van bedieningsvorm, montagewijze en toepassing per type gordijnrail
              </caption>
              <thead data-id="element-452" data-mp="28cy7mk12z4xx">
                <tr className="border-b border-white/20 text-[13px] uppercase tracking-[0.14em] text-white/50" data-id="element-453" data-mp="23ghl1u9j4ymy">
                  <th scope="col" className="py-4 pr-6 font-500" data-id="element-454" data-mp="ixx780s1od98">Situatie</th>
                  <th scope="col" className="py-4 pr-6 font-500" data-id="element-455" data-mp="nolzvjfgng9y">Voor de hand liggende keuze</th>
                  <th scope="col" className="py-4 font-500" data-id="element-456" data-mp="9xqystb7e3gx">Waar het op aankomt</th>
                </tr>
              </thead>
              <tbody className="text-[16px]" data-id="element-457" data-mp="dyaxi1bfdf56">
                {[{
                situation: 'Normale raamhoogte, bereikbaar, één of twee banen',
                choice: 'Gordijnrailsystemen (handbediend)',
                focus: 'Soepele loop, montagewijze en de zichtbaarheid van het profiel'
              }, {
                situation: 'Hoge of brede raampartij, moeilijk bereikbaar',
                choice: 'Elektrische gordijnrails',
                focus: 'Voeding, kabelroute en het geluidsniveau van de aandrijving'
              }, {
                situation: 'Meerdere gordijnen die samen moeten bewegen',
                choice: 'Gemotoriseerde gordijnrails',
                focus: 'Scenario\'s, eindposities en dimensionering op stofgewicht'
              }, {
                situation: 'Veel repeterende ruimtes, bouwfasering',
                choice: 'Projectmatige gordijnrails',
                focus: 'Maatvoering op tekening en levering per bouwdeel'
              }, {
                situation: 'Bed- of behandelplek met privacygordijn',
                choice: 'Zorggordijnrails',
                focus: 'Intensief gebruik, reiniging en gebogen railverloop'
              }, {
                situation: 'Hotel, kantoor, zaal of horeca',
                choice: 'Commerciële gordijnrails',
                focus: 'Belasting, ruimteverdeling en centrale bediening'
              }].map(row => <tr key={row.situation} className="border-b border-white/10 align-top" data-id="element-458" data-mp="1ajxahqkrzx4l">
                    <th scope="row" className="py-5 pr-6 font-500 text-white" data-id="element-459" data-mp="1hyzjbyzq65fo">{row.situation}</th>
                    <td className="py-5 pr-6 text-white/80" data-id="element-460" data-mp="1my2cal2fkl7a">{row.choice}</td>
                    <td className="py-5 text-white/60" data-id="element-461" data-mp="1rthnt6cyu50j">{row.focus}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="mt-10 text-[16px] text-white/70" data-id="element-462" data-mp="20wad542l86nq">
            Twijfelt u tussen twee uitvoeringen?{' '}
            <Link to="/contact" className="font-semibold text-white underline decoration-accent underline-offset-4" data-id="element-463" data-mp="25tsg8dtxbrnt">
              Neem contact op
            </Link>{' '}
            en leg de situatie voor. Achtergrondinformatie en het volledige internationale productoverzicht staan op{' '}
            <a href={business.website} target="_blank" rel="noopener" className="font-semibold text-white underline decoration-accent underline-offset-4" data-id="element-464" data-mp="2kkj9lqb5yyt1">
              {business.websiteLabel}
            </a>
            .
          </p>
        </div>
      </section>

      <section className="border-b border-line bg-white" aria-labelledby="verder-lezen" data-id="element-465" data-mp="2jlwag5a0ggf3">
        <div className="mx-auto max-w-shell px-6 py-16" data-id="element-466" data-mp="2oj4uycimswji">
          <h2 id="verder-lezen" className="font-display text-2xl font-700 tracking-tightest text-ink md:text-[30px]" data-id="element-467" data-mp="2tbxyix2d5yu0">
            Verder lezen
          </h2>
          <ul className="mt-8 grid gap-6 md:grid-cols-3" data-id="element-468" data-mp="2yclnzeay27m4">
            {[{
            to: '/over-ons',
            title: 'Over Goelst International',
            text: 'Onze achtergrond sinds 1978 en de manier waarop wij techniek en vormgeving combineren.'
          }, {
            to: '/resources',
            title: 'Resources en vermeldingen',
            text: 'Gecontroleerde bedrijfsprofielen, kaartvermeldingen en mediakanalen van Goelst International.'
          }, {
            to: '/contact',
            title: 'Contact en route',
            text: 'Adres, telefoonnummer, e-mail, openingstijden en de locatie aan de Modemweg in Amersfoort.'
          }].map(card => <li key={card.to} className="border-t-2 border-ink pt-5" data-id="element-469" data-mp="1aekwq22x4k20">
                <h3 className="font-display text-[20px] font-600 text-ink" data-id="element-470" data-mp="15kgq5jxkee1g">
                  <Link to={card.to} className="transition-colors hover:text-accent" data-id="element-471" data-mp="10o020pqd7zod">
                    {card.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-472" data-mp="1tbm0xkoneg4w">{card.text}</p>
                <Link to={card.to} className="mt-4 inline-flex items-center gap-2 text-[15px] font-semibold text-accent transition-colors hover:text-accent-dark" data-id="element-473" data-mp="1ocj9x45i6afx">
                  Bekijken
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-474" data-mp="1edbmtp8yv1fm" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <ContactBand heading="Vraag een advies aan voor uw gordijnrails" text="Stuur ons de raammaten, de gewenste bediening en indien beschikbaar de plafondtekening. Wij reageren met een voorstel voor de meest geschikte uitvoering." data-id="element-475" data-mp="2zg8gipvywpg" />
    </>;
}